import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import classNames from 'classnames';
import Loading, { LoadingScale } from 'bloko/blocks/loading';
import { P as PostMessage } from '../postMessage-9d8eba93.js';
import { V as Viewer, s as styleInject } from '../index-87699efb.js';
import LocalStorage from 'bloko/common/storage/LocalStorageWrapper';
import { _ as __assign } from '../_tslib-3301712a.js';
import { getBreakpoint } from 'bloko/common/media';
import 'react-dom';
import 'bloko/common/hooks/useBreakpoint';

// Компонент, слушающий события от iframe и предназначенный для отображения viewer в режиме работы с iframe
var IframeViewer = function (_a) {
    var origin = _a.origin;
    var _b = useState(), viewerImageInfo = _b[0], setViewerImageInfo = _b[1];
    useEffect(function () {
        return PostMessage.on({
            origin: origin,
            handlers: {
                imageOpen: function (data) {
                    setViewerImageInfo(data);
                },
            },
        });
    }, [origin]);
    return viewerImageInfo ? (jsx(Viewer, { imageUrl: viewerImageInfo.image.url, imageOriginalWidth: viewerImageInfo.image.width, imageOriginalHeight: viewerImageInfo.image.height, title: viewerImageInfo.viewerTitle, subtitle: viewerImageInfo.viewerSubtitle, onClose: function () { return setViewerImageInfo(undefined); } })) : null;
};

var CHATIK_EVENT = {
    ACTIVATE: 'activate',
    DEACTIVATE: 'deactivate',
    NAVIGATE: 'navigate',
    PARENT_BREAKPOINT_CHANGE: 'parentBreakpointChange',
};
var DEBUG_DATA_LS_KEY = 'chatik_debug_last_opened_data';
var DEBUG_EVENT = {
    PAGE_RECEIVED: '@@debug/pageReceived',
    INITED: '@@debug/inited',
    WEBSOCKET_CONNECTED: '@@debug/websocketConnected',
    MAIN_SCRIPT_LOADED: '@@debug/mainScriptLoaded',
    ERROR: '@@debug/error',
    PAGE_LOADING_ERROR: '@@debug/pageLoadingError',
};

var getDebugData = function () {
    var data = LocalStorage.getItem(DEBUG_DATA_LS_KEY);
    try {
        return JSON.parse(data);
    }
    catch (_) {
        return null;
    }
};
var updateDebugData = function (debugData, event) {
    var eventData = event.data;
    /**
     * Если данных еще нет или requestId не совпадает в сохраненных данных и в собитии,
     * то считаем что это новая сессия и заменяем данные на актульаные.
     */
    if (debugData === null || debugData.requestId !== eventData.requestId) {
        return {
            requestId: eventData.requestId,
            data: {
                events: [event.name],
                wasWebsocketConnected: event.name === DEBUG_EVENT.WEBSOCKET_CONNECTED,
                // @ts-expect-error не победил типы
                wasMainScriptLoaded: event.name === DEBUG_EVENT.MAIN_SCRIPT_LOADED && Boolean(eventData.ok),
                wasInited: event.name === DEBUG_EVENT.INITED,
                isFrameInViewport: 
                // @ts-expect-error не победил типы
                event.name === DEBUG_EVENT.PAGE_RECEIVED ? Boolean(eventData.isInViewport) : undefined,
                errors: [DEBUG_EVENT.ERROR, DEBUG_EVENT.PAGE_LOADING_ERROR].includes(event.name)
                    ? [eventData]
                    : [],
            },
        };
    }
    /**
     * Если requestId совпадает в сохраненных данных и в собитии, то обновляем существующие данные
     */
    debugData.data.events.push(event.name);
    switch (event.name) {
        case DEBUG_EVENT.PAGE_RECEIVED:
            // @ts-expect-error не победил типы
            debugData.data.isFrameInViewport = Boolean(eventData.isInViewport);
            break;
        case DEBUG_EVENT.WEBSOCKET_CONNECTED:
            debugData.data.wasWebsocketConnected = true;
            break;
        case DEBUG_EVENT.MAIN_SCRIPT_LOADED:
            // @ts-expect-error не победил типы
            debugData.data.wasMainScriptLoaded = Boolean(eventData.ok);
            break;
        case DEBUG_EVENT.INITED:
            debugData.data.wasInited = true;
            break;
        case DEBUG_EVENT.ERROR:
            if (!debugData.data.errors) {
                debugData.data.errors = [];
            }
            debugData.data.errors.push(eventData);
            break;
    }
    return debugData;
};
var writeDebugEvent = function (name, data) {
    var currentDebugData = getDebugData();
    var updatedDebugData = updateDebugData(currentDebugData, { name: name, data: data });
    LocalStorage.setItem(DEBUG_DATA_LS_KEY, JSON.stringify(updatedDebugData));
};

var DELAY_MS = 15000;
/**
 * В случае сетевых ошибок у айфрейма не срабатывает событие onload
 * onerror у айфрейма вообще нет.
 * Поэтому если загрузка не закончилась через 15 секунд после начала,
 * делаем еще один запрос по тому же url в надежде получить какую то информацию об ошибке.
 */
var useLogPageLoadingError = function (_a) {
    var loaded = _a.loaded, url = _a.url;
    var timeoutIdRef = useRef(null);
    useEffect(function () {
        if (!loaded) {
            timeoutIdRef.current = setTimeout(function () {
                fetch(url).catch(function (error) {
                    writeDebugEvent('@@debug/pageLoadingError', {
                        requestId: null,
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    });
                });
            }, DELAY_MS);
        }
        if (loaded && timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
    }, [loaded, url]);
};

var css_248z = "/* stylelint-disable hhru/less-variable-value-disallowed-list */\n/* Design System colors */\n/* Red */\n/* Yellow */\n/* Orange */\n/* Blue */\n/* Violet */\n/* Green */\n/* Grayscale */\n/* Special */\n.chatik-integration-iframe-container {\n  position: absolute;\n  top: 0;\n  right: 0;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  height: 100%;\n  background: #ffffff;\n}\n.chatik-integration-iframe {\n  position: absolute;\n  top: 0;\n  right: 0;\n  visibility: hidden;\n  height: 100%;\n  width: 100%;\n}\n.chatik-integration-iframe_loaded {\n  visibility: visible;\n}\n";
styleInject(css_248z);

var Iframe = memo(React.forwardRef(function (_a, ref) {
    var url = _a.url, origin = _a.origin;
    var _b = useState(false), loaded = _b[0], setLoaded = _b[1];
    useLogPageLoadingError({ loaded: loaded, url: url });
    var style = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
    return (jsxs(Fragment, { children: [jsxs("div", { style: style, className: "chatik-integration-iframe-container", children: [!loaded && jsx(Loading, { scale: LoadingScale.Medium }), jsx("iframe", { className: classNames('chatik-integration-iframe', {
                            'chatik-integration-iframe_loaded': loaded,
                        }), src: url, ref: ref, style: loaded ? {} : { position: 'absolute', visibility: 'hidden' }, onLoad: function () { return setLoaded(true); } })] }), jsx(IframeViewer, { origin: origin })] }));
}));

var isElementInViewport = function (element) {
    var _a = element.getBoundingClientRect(), top = _a.top, left = _a.left, bottom = _a.bottom, right = _a.right;
    var innerHeight = window.innerHeight, innerWidth = window.innerWidth;
    return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};

var initIncomingMessageHandlers = function (_a) {
    var _b;
    var origin = _a.origin, iframeRef = _a.iframeRef, resolveChatikReadyPromise = _a.resolveChatikReadyPromise, chatikIFrameReadyPromise = _a.chatikIFrameReadyPromise, _c = _a.incomingMessageHandlers, incomingMessageHandlers = _c === void 0 ? {} : _c;
    return PostMessage.on({
        origin: origin,
        // @ts-expect-error не победил типы
        handlers: __assign(__assign({}, incomingMessageHandlers), (_b = { awaitingInit: function () {
                    void chatikIFrameReadyPromise.then(function (chatikWindow) {
                        PostMessage.trigger({
                            target: chatikWindow,
                            message: { name: 'init' },
                        });
                    });
                }, ready: function () {
                    resolveChatikReadyPromise();
                } }, _b[DEBUG_EVENT.PAGE_RECEIVED] = function (data) {
            if (iframeRef.current) {
                data.isInViewport = isElementInViewport(iframeRef.current);
            }
            writeDebugEvent(DEBUG_EVENT.PAGE_RECEIVED, data);
        }, _b[DEBUG_EVENT.INITED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.INITED, data);
        }, _b[DEBUG_EVENT.WEBSOCKET_CONNECTED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.WEBSOCKET_CONNECTED, data);
        }, _b[DEBUG_EVENT.MAIN_SCRIPT_LOADED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.MAIN_SCRIPT_LOADED, data);
        }, _b[DEBUG_EVENT.ERROR] = function (data) {
            writeDebugEvent(DEBUG_EVENT.ERROR, data);
        }, _b)),
    });
};

var resolveChatikReadyPromise;
var resolveChatikIFrameReadyPromise;
/** Промис зарезолвится когда чатик отправит post message 'ready' */
var chatikReadyPromise = new Promise(function (resolve) { return (resolveChatikReadyPromise = resolve); });
/** Промис зарезолвится когда у айфрейма случится событие load */
var chatikIFrameReadyPromise = new Promise(function (resolve) { return (resolveChatikIFrameReadyPromise = resolve); });
var useInitCommunicationWithIframe = function (_a) {
    var origin = _a.origin, iframeRef = _a.iframeRef, messageHandlers = _a.messageHandlers;
    useEffect(function () {
        chatikReadyPromise = new Promise(function (resolve) { return (resolveChatikReadyPromise = resolve); });
        chatikIFrameReadyPromise = new Promise(function (resolve) { return (resolveChatikIFrameReadyPromise = resolve); });
        var removeHandlersListeners = initIncomingMessageHandlers({
            origin: origin,
            iframeRef: iframeRef,
            resolveChatikReadyPromise: resolveChatikReadyPromise,
            chatikIFrameReadyPromise: chatikIFrameReadyPromise,
            incomingMessageHandlers: messageHandlers,
        });
        return function () {
            removeHandlersListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        var handleLoad = function () {
            var _a;
            var chatikWindow = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
            if (chatikWindow) {
                resolveChatikIFrameReadyPromise(chatikWindow);
            }
        };
        var iframe = iframeRef.current;
        iframe === null || iframe === void 0 ? void 0 : iframe.addEventListener('load', handleLoad);
        return function () {
            iframe === null || iframe === void 0 ? void 0 : iframe.removeEventListener('load', handleLoad);
        };
    }, [iframeRef]);
    return useCallback(function (name, data) {
        void chatikReadyPromise.then(function () {
            var _a;
            var chatikWindow = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
            chatikWindow && PostMessage.trigger({ target: chatikWindow, message: { name: name, data: data } });
        });
    }, [iframeRef]);
};

var getOriginFromUrl = function (url) {
    try {
        return new URL(url).origin;
    }
    catch (_a) {
        return url;
    }
};

var useCommunicateWithEmbeddedIframe = function (_a) {
    var url = _a.url, iframeRef = _a.iframeRef, messageHandlers = _a.messageHandlers;
    var sendToChatik = useInitCommunicationWithIframe({ iframeRef: iframeRef, origin: getOriginFromUrl(url), messageHandlers: messageHandlers });
    useEffect(function () {
        var _a, _b;
        // @ts-expect-error тип window.globalVars определен в xhh
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        var hhtmFrom = ((_b = (_a = window.globalVars) === null || _a === void 0 ? void 0 : _a.analyticsParams) === null || _b === void 0 ? void 0 : _b.hhtmSource) || null;
        var pushPermission = 'Notification' in window ? Notification.permission : null;
        var urlParams = new URLSearchParams(window.location.search);
        var vacancyId = urlParams.get('vacancyId');
        var collection = urlParams.get('collection');
        var hhtmFromLabel = urlParams.get('hhtmFromLabel');
        sendToChatik(CHATIK_EVENT.ACTIVATE, {
            type: 'embedded',
            hhtmFrom: hhtmFrom,
            hhtmFromLabel: hhtmFromLabel,
            vacancyId: vacancyId,
            collection: collection,
            pushPermission: pushPermission,
            parentScreenBreakpoint: getBreakpoint(),
            currentUrl: window.location.href,
        });
        // Встроенный чатик активируется при открытии страницы 1 раз
    }, [sendToChatik]);
};

var EmbeddedChatIframe = function (_a) {
    var url = _a.url, handlers = _a.handlers;
    var iframeRef = useRef(null);
    useCommunicateWithEmbeddedIframe({ url: url, iframeRef: iframeRef, messageHandlers: handlers });
    return jsx(Iframe, { origin: getOriginFromUrl(url), ref: iframeRef, url: url });
};

export { EmbeddedChatIframe };
//# sourceMappingURL=index.js.map
